/**
 * urlKeys.ts — remove ONE key from a URL's query and fragment, and change
 * nothing else about either of them.
 *
 * WHY THIS IS NOT `URLSearchParams` (2026-08-28). The hand-off scrubs used to
 * delete their key by round-tripping the search AND the hash through
 * `URLSearchParams` — parse, delete, `toString()`. That is correct for a query
 * string and wrong for a fragment, because a fragment is not required to be a
 * key/value list at all. `/weightloss?lead=x#faq` came back as
 * `/weightloss#faq=`: `URLSearchParams('faq')` reads a valueless parameter and
 * re-serialises it with the `=` it never had. A plain anchor is a legitimate
 * thing for a marketing link to carry, and the scrub silently rewrote it into a
 * different one — on a page with a matching `id`, a broken one.
 *
 * The re-serialisation is lossy in smaller ways too, in the query as well as
 * the fragment: it re-encodes reserved characters, turns `+` into a space and
 * back, and normalises empty segments. None of that is the scrub's business.
 * Its job is to remove one key from someone else's URL and hand the rest back
 * untouched — attribution tags (`channel`/`campaign`/`v`) included, since they
 * are what the funnel this whole change exists to fix is measured on.
 *
 * So: split on `&`, drop the segments whose NAME matches, rejoin. Every
 * surviving segment is byte-for-byte what arrived. Reading a key still goes
 * through `URLSearchParams` (see `leadHandoff.ts` / `resumeHandoff.ts`) —
 * lenient parsing is right for a read and wrong for a rewrite.
 */
import type { Location } from 'react-router-dom';

/**
 * The name a `key=value` segment declares, percent-decoded so `%6Cead` cannot
 * smuggle a `lead` past the filter. A segment with no `=` is all name and no
 * value — that is what `#faq` is.
 *
 * A malformed escape (`%zz`) makes `decodeURIComponent` throw; the raw text is
 * then compared instead, which can only fail to match and therefore only ever
 * KEEPS a segment. Keeping an unrecognisable segment is the safe direction — it
 * cannot be one of ours, because ours are minted by `encodeURIComponent`.
 */
function segmentName(segment: string): string {
  const eq = segment.indexOf('=');
  const raw = eq === -1 ? segment : segment.slice(0, eq);
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

/**
 * Drop every `key=…` segment from a raw query/fragment body, verbatim otherwise.
 * `split('&').join('&')` is the identity on anything left alone, which is the
 * property that makes "unchanged" mean unchanged.
 */
function dropKey(body: string, key: string): string {
  if (!body) return '';
  return body
    .split('&')
    .filter((segment) => segmentName(segment) !== key)
    .join('&');
}

/**
 * The search + hash this location would have with every `key` pair removed.
 *
 * Returns empty strings rather than a bare `?` or `#`, so a fully-stripped URL
 * is `/weightloss` and not `/weightloss?#`.
 */
export function stripKeyFromLocation(
  location: Pick<Location, 'search' | 'hash'>,
  key: string,
): { search: string; hash: string } {
  const search = dropKey(location.search.replace(/^\?/, ''), key);
  const hash = dropKey(location.hash.replace(/^#/, ''), key);
  return { search: search ? `?${search}` : '', hash: hash ? `#${hash}` : '' };
}
